import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { trpc } from '@/api/trpc';
import { useAuth } from '@/context/AuthContext';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import { FormCheckbox, Button, Alert, TradingStrategySelect } from '@/components/UI';

import type { BarDuration, ChartMarker, EntityTypeUnion, TradingStrategy } from '@einhaus/project314-types';

interface StrategyEntriesSelectorProps {
    entity: EntityTypeUnion;
    startDateTime: string;
    endDateTime: string;
    duration: BarDuration;
    onMarkersChange: (markers: ChartMarker[], legend?: string) => void;
}

/**
 * Converts trade entries to chart markers
 */
const convertEntriesToMarkers = (entries: { dateTime: string; direction: 'long' | 'short' }[]): ChartMarker[] => {
    return entries.map((entry) => ({
        dateTime: entry.dateTime,
        text: entry.direction === 'long' ? 'Long position entry' : 'Short position entry',
        position: entry.direction === 'long' ? 'aboveBar' : 'belowBar'
    }));
};

const renderEntriesStatus = (config: {
    hasSelectedStrategy: boolean;
    isLoading: boolean;
    hasError: boolean;
    entryCount: number | null;
    onRetry: () => unknown;
}) => {
    const { hasSelectedStrategy, isLoading, hasError, entryCount, onRetry } = config;

    if (!hasSelectedStrategy) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <div
                    aria-label="Loading entries"
                    className="animate-spin h-4 w-4 border-2 border-zinc-400 rounded-full border-t-blue-500"
                    role="status"
                />
                <span>Loading entries...</span>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="flex items-center gap-2 text-red-400 text-sm">
                <span>Failed to load entries</span>
                <Button size="sm" variant="dark" onClick={onRetry}>
                    Retry
                </Button>
            </div>
        );
    }

    if (entryCount === null) {
        return null;
    }

    if (entryCount === 0) {
        return <div className="text-zinc-400 text-sm">No entries found for this date range</div>;
    }

    return (
        <div className="text-green-400 text-sm">
            {entryCount} {entryCount === 1 ? 'entry' : 'entries'} loaded
        </div>
    );
};

/**
 * Component for selecting a trading strategy and displaying its entries on the chart.
 * Only visible to authenticated users.
 */
const StrategyEntriesSelector: React.FC<StrategyEntriesSelectorProps> = ({
    entity,
    startDateTime,
    endDateTime,
    duration,
    onMarkersChange
}) => {
    const { isAuthenticated } = useAuth();
    const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
    const [applyScorecard, setApplyScorecard] = useState(false);

    // Shared strategies query (deduped with the one inside TradingStrategySelect)
    const { isLoading: isLoadingStrategies, strategies } = useTradingStrategies({ enabled: isAuthenticated });

    // Only active strategies whose allowed entity types include the current entity
    const isStrategyAvailable = useCallback(
        (strategy: TradingStrategy) => strategy.active && strategy.allowedUnderlyingEntityTypes.includes(entity.entityType),
        [entity.entityType]
    );

    // Get selected strategy object for duration comparison and legend name
    const selectedStrategy = useMemo(() => {
        return strategies.find((s) => s.id === selectedStrategyId) || null;
    }, [strategies, selectedStrategyId]);

    // Duration mismatch check
    const hasDurationMismatch = selectedStrategy && selectedStrategy.duration !== duration;
    const shouldUseScorecardedFetch = applyScorecard && selectedStrategyId !== null;
    const shouldUseTradeLogicV2Fetch = !applyScorecard && selectedStrategyId !== null;

    const scorecardedEntriesQuery = useAppQuery({
        ...trpc.strategy.fetchScorecardedStrategyEntries.queryOptions({
            strategyId: selectedStrategyId!,
            entityId: entity.id,
            entityType: entity.entityType,
            startDateTime,
            endDateTime
        }),
        enabled: isAuthenticated && !!selectedStrategyId && shouldUseScorecardedFetch
    });

    const tradeLogicV2EntriesQuery = useAppQuery({
        ...trpc.strategy.fetchStrategyEntriesV2.queryOptions({
            strategyId: selectedStrategyId!,
            entityId: entity.id,
            entityType: entity.entityType,
            startDateTime,
            endDateTime
        }),
        enabled: isAuthenticated && !!selectedStrategyId && shouldUseTradeLogicV2Fetch
    });

    const scorecardedEntriesData = typeof scorecardedEntriesQuery.data === 'function' ? null : scorecardedEntriesQuery.data;
    const tradeLogicV2EntriesData = typeof tradeLogicV2EntriesQuery.data === 'function' ? null : tradeLogicV2EntriesQuery.data;

    const activeEntriesData = applyScorecard ? scorecardedEntriesData : tradeLogicV2EntriesData;

    const activeEntries = activeEntriesData?.entries ?? [];
    const hasResolvedActiveEntries = activeEntriesData?.entries !== undefined;

    const isActiveEntriesLoading = shouldUseTradeLogicV2Fetch
        ? tradeLogicV2EntriesQuery.isLoading
        : shouldUseScorecardedFetch
          ? scorecardedEntriesQuery.isLoading
          : false;

    const activeEntriesError = shouldUseTradeLogicV2Fetch
        ? tradeLogicV2EntriesQuery.error
        : shouldUseScorecardedFetch
          ? scorecardedEntriesQuery.error
          : null;

    const refetchActiveEntries = shouldUseTradeLogicV2Fetch
        ? tradeLogicV2EntriesQuery.refetch
        : shouldUseScorecardedFetch
          ? scorecardedEntriesQuery.refetch
          : undefined;

    // Convert entries to markers when data changes
    useEffect(() => {
        if (!isAuthenticated || !selectedStrategyId) {
            return;
        }

        if (hasResolvedActiveEntries) {
            const markers = convertEntriesToMarkers(activeEntries);
            onMarkersChange(markers, selectedStrategy?.name);
        }
    }, [activeEntries, hasResolvedActiveEntries, selectedStrategy?.name, selectedStrategyId, onMarkersChange, isAuthenticated]);

    // Reset only this selector's selection when the entity changes. Mounting a
    // hidden Strategy tab must not clear markers supplied by the chart's caller.
    useEffect(() => {
        if (!isAuthenticated || selectedStrategyId === null) {
            return;
        }

        setSelectedStrategyId(null);
        onMarkersChange([], undefined);
    }, [entity.id, entity.entityType, isAuthenticated]);

    // Handle strategy selection change
    const handleStrategyChange = useCallback(
        (strategyId: number | null) => {
            if (strategyId === null) {
                setSelectedStrategyId(null);
                onMarkersChange([], undefined);
            } else {
                setSelectedStrategyId(strategyId);
            }
        },
        [onMarkersChange]
    );

    // Handle scorecard checkbox change
    const handleScorecardChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setApplyScorecard(e.target.checked);
    }, []);

    // Handle clear button
    const handleClear = useCallback(() => {
        setSelectedStrategyId(null);
        setApplyScorecard(false);
        onMarkersChange([], undefined);
    }, [onMarkersChange]);

    const statusMessage = renderEntriesStatus({
        hasSelectedStrategy: selectedStrategyId !== null,
        isLoading: isActiveEntriesLoading,
        hasError: activeEntriesError !== null,
        entryCount: selectedStrategyId !== null && hasResolvedActiveEntries ? activeEntries.length : null,
        onRetry: () => {
            if (refetchActiveEntries) {
                refetchActiveEntries().catch(() => undefined);
            }
        }
    });

    // Don't render if not authenticated (after all hooks)
    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="p-3 bg-[#1a1a1a] rounded-lg border border-zinc-700">
            <div className="text-sm font-medium text-zinc-300 mb-2">Strategy Entries</div>

            <div className="flex flex-wrap items-center gap-3">
                {/* Strategy dropdown */}
                <div className="flex-1 min-w-[200px]">
                    <TradingStrategySelect
                        noMargin
                        aria-label="Select trading strategy"
                        filter={isStrategyAvailable}
                        isDisabled={isLoadingStrategies}
                        size="sm"
                        value={selectedStrategyId}
                        onChange={handleStrategyChange}
                    />
                </div>

                {/* Scorecard checkbox */}
                <FormCheckbox
                    checked={applyScorecard}
                    disabled={!selectedStrategyId}
                    id="applyScorecard"
                    label="Apply Scorecard"
                    onChange={handleScorecardChange}
                />

                {/* Clear button */}
                {selectedStrategyId && (
                    <Button size="sm" variant="dark" onClick={handleClear}>
                        Clear
                    </Button>
                )}
            </div>

            {/* Duration mismatch warning */}
            {hasDurationMismatch && selectedStrategy && (
                <Alert className="mt-2 py-2" variant="warning">
                    Duration mismatch: Strategy uses <strong>{selectedStrategy.duration}</strong> bars, chart shows{' '}
                    <strong>{duration}</strong> bars. Markers may not align with candles.
                </Alert>
            )}

            {/* Status message */}
            <div className="mt-2">{statusMessage}</div>
        </div>
    );
};

export default StrategyEntriesSelector;
